// Libraries
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Redux
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { clearSession, setDataCompany } from '@redux/session/session.slice';
import { setEmployerId, setInformation } from '@redux/company/company.slice';
import { getDynamicData, setLoaderRegisterCompany } from '@redux/utils/utils.slice';
import {
    checkCompanyOnboardingInformation,
    getAllPendingDocumentsToBeApprove,
    setModalErroRequest,
    setUserId,
} from '@redux/onboarding/onboarding.slice';
// Api
import { apiPostSecurityEmployer } from '@api/session';
import { apiGetCompany } from '@api/company';
// Hooks
import { useOnboardingActions } from '@hooks/useOnboardingActions';
// Models
import { RootState } from '@redux/rootReducer';
import { IOptionSelect } from '@components/input';
import { IDataCompany } from '../models/DataCompany';
import { TypesDocumentIds } from '@pages/employees/constants/Employees';
import { ICompanyInformation, ICompanyRegistration } from '@models/Company';
// Constants
import { ALL_PPRODUCT_NAMES, OnboardingStepsUser, TypeDocumentNames } from '@constants/Onboarding';
// Utils
import { createFreeDemoTransaction, sendWelcomeNotification, getActivePlans } from '@redux/plans/plans.slice';
import { useActionsLogin } from '@pages/home/hooks/useActionsLogin';

export const useDataCompany = (): {
    plansOptions: IOptionSelect[];
    documentTypeOptions: IOptionSelect[];
    openCompanyErrorMessage: boolean;
    showLoader: boolean;
    showCustomLoader: boolean;
    currentStep: number | null;
    modalErrorRequest: boolean | null;
    loader: boolean;
    shouldShowFreeDemo: boolean;
    handleCompanyCreationSubmit: (values: IDataCompany) => void;
    handleSubmitDocumentsApprovement: () => void;
    openDocumentPdf: (documentName: string, documentProduct: string) => void;
    handleRefreshPage: () => void;
    handleLogOut: () => void;
} => {
    const {
        onboarding: { dataProcessing, step, modalErrorRequest },
        utils: { document_type, loaderRegisterCompany, loader },
        session: { user },
        company: { id, information },
        plans: { activePlans: platformPlans },
    } = useAppSelector((state: RootState) => state);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { openDocumentPdf, approveDocuments } = useOnboardingActions();
    const { handleLogoutRedirect } = useActionsLogin();

    const { COMPANY_DATA } = OnboardingStepsUser;

    const [plansOptions, setPlansOptions] = useState<IOptionSelect[]>([]);
    const [documentTypeOptions, setDocumentTypeOptions] = useState<IOptionSelect[]>([]);
    const [shouldShowFreeDemo, setShouldShowFreeDemo] = useState<boolean>(false);
    const [openCompanyErrorMessage, setOpenCompanyErrorMessage] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(true);
    const [companyIsRegistering, setCompanyIsRegistering] = useState<boolean>(false);

    useEffect(() => {
        if (id && !companyIsRegistering) checkStatus();
    }, [id]);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            if (step == COMPANY_DATA) {
                dispatch(getDynamicData(['document_type']));
                dispatch(getAllPendingDocumentsToBeApprove());
                dispatch(getActivePlans());
            }
        };
        fetchData();
        setShowLoader(false);
    }, [step]);

    useEffect(() => {
        const alreadyAcceptDataProcessing = information.users ? information.users[0].ael_data_processing_policy : false;
        if (!alreadyAcceptDataProcessing) {
            setShouldShowFreeDemo(true);
        }
    }, [dataProcessing]);

    useEffect(() => {
        if (document_type?.length > 0) {
            const newData: IOptionSelect[] = document_type.map(element => {
                return {
                    label: element?.name || '',
                    value: element?.id || '',
                };
            });
            setDocumentTypeOptions(
                newData.filter(item => item.value === TypesDocumentIds.NIT || item.value === TypesDocumentIds.CC)
            );
        }
    }, [document_type]);

    useEffect(() => {
        if (platformPlans.length > 0) {
            const formatedPlanOptions: IOptionSelect[] = platformPlans.map(plan => {
                return {
                    label: `${plan.employees} Empleados`,
                    value: plan.id,
                };
            });
            setPlansOptions(formatedPlanOptions);
        }
    }, [platformPlans]);

    const checkStatus = async (): Promise<void> => {
        const MAIN_USER = 0;
        const response = await apiGetCompany<ICompanyInformation>(id);
        const user = response?.users && response?.users[MAIN_USER];
        dispatch(setUserId(user?.user_id));
        response && dispatch(setInformation(response));
        const documentType = response.document_type_id === TypesDocumentIds.NIT ? 'NIT' : 'CC';
        const documentNumber = response.document_number ? response.document_number : '';
        dispatch(checkCompanyOnboardingInformation(documentType, documentNumber, navigate));
    };

    const buildCompanyName = (values: IDataCompany): string => {
        const { first_name, second_name, first_last_name, second_last_name, company_name, document_type_id } = values;

        if (document_type_id === TypesDocumentIds.NIT) return company_name!;

        const fullName = [first_last_name, second_last_name, first_name, second_name].filter(Boolean).join(' ');

        return fullName;
    };

    const handleCompanyCreationSubmit = async (values: IDataCompany): Promise<void> => {
        const { document_type_id, document_number, dv, plan_type_id } = values;

        setCompanyIsRegistering(true);
        const documentNumberFormated = document_type_id === TypesDocumentIds.NIT ? `${document_number}${dv}` : document_number;

        dispatch(setLoaderRegisterCompany(true));
        const dataFormat = {
            company_name: buildCompanyName(values),
            document_number: documentNumberFormated,
            document_type_id: document_type_id,
        };

        const PostNewCompanyResponse = await apiPostSecurityEmployer<ICompanyRegistration>(dataFormat);

        if (typeof PostNewCompanyResponse === 'string') {
            setOpenCompanyErrorMessage(true);
            return;
        }

        const company = await apiGetCompany<ICompanyInformation>(PostNewCompanyResponse.id);
        dispatch(setInformation(company));
        dispatch(setEmployerId(PostNewCompanyResponse.id));
        dispatch(setUserId(PostNewCompanyResponse.user_id));
        dispatch(setDataCompany(false));
        await handleApprovePendingDocuments(
            PostNewCompanyResponse.document_type_id,
            documentNumberFormated,
            PostNewCompanyResponse.user_id,
            true
        );
        await dispatch(createFreeDemoTransaction(plan_type_id));

        const userEmail = company?.users?.[0]?.email;

        if (userEmail && PostNewCompanyResponse.user_id) {
            await dispatch(sendWelcomeNotification(PostNewCompanyResponse.user_id, userEmail));
        }

        const documenType = PostNewCompanyResponse.document_type_id === TypesDocumentIds.NIT ? 'NIT' : 'CC';
        await dispatch(checkCompanyOnboardingInformation(documenType, PostNewCompanyResponse.document_number, navigate));
    };

    const handleSubmitDocumentsApprovement = async (): Promise<void> => {
        const companyUserId = information.users && information.users[0].user_id ? information.users[0].user_id : null;
        const documentNumber = information.document_number ? information.document_number : null;
        const documentType = information.document_type_id ? information.document_type_id : null;
        if (companyUserId && documentNumber && documentType) {
            await handleApprovePendingDocuments(documentType, documentNumber, companyUserId);
            return;
        }
        dispatch(setModalErroRequest(true));
    };

    const handleApprovePendingDocuments = async (
        documentTypeid: string,
        documentNumber: string,
        documentUserId: string,
        includeFreeDemoDocument = false
    ): Promise<void> => {
        const documentNames = includeFreeDemoDocument
            ? [TypeDocumentNames.DATA_PROCESSING, TypeDocumentNames.FREE_DEMO]
            : [TypeDocumentNames.DATA_PROCESSING];
        const documenType = documentTypeid === TypesDocumentIds.NIT ? 'NIT' : 'CC';
        await approveDocuments(documentUserId, documenType, documentNumber, documentNames, ALL_PPRODUCT_NAMES, false);
    };

    const handleLogOut = (): void => {
        user?.homeAccountId && dispatch(clearSession());
        handleLogoutRedirect();
    };

    const handleRefreshPage = (): void => {
        window.location.reload();
    };

    return {
        plansOptions,
        documentTypeOptions,
        openCompanyErrorMessage,
        showLoader,
        showCustomLoader: loaderRegisterCompany,
        currentStep: step,
        modalErrorRequest,
        loader,
        shouldShowFreeDemo,
        handleCompanyCreationSubmit,
        handleSubmitDocumentsApprovement,
        handleLogOut,
        openDocumentPdf,
        handleRefreshPage,
    };
};
